// src/components/BotLauncher/BotLauncher.tsx
import React, { useState, useCallback } from "react";
import Chatbot from "@/components/Chatbot";

type Mode = "closed" | "choosing" | "calling" | "chatting";

interface PhoneNumber {
  id: string;
  label: string;
  number: string;
  icon: string;
}

const phoneNumbers: PhoneNumber[] = [
  { id: "support", label: "Support", number: "+1 (321) 519-4247", icon: "💼" },
];

// ── Icons ────────────────────────────────────────────────────────
const HeadsetIcon = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 1C7.03 1 3 5.03 3 10v1H1v4a2 2 0 002 2h1v-6H3.07C3.56 6.18 7.4 3 12 3s8.44 3.18 8.93 8H20v6h1a2 2 0 002-2v-4h-2v-1c0-4.97-4.03-9-9-9zm-2 11a1.5 1.5 0 010 3 1.5 1.5 0 010-3zm4 0a1.5 1.5 0 010 3 1.5 1.5 0 010-3z" />
  </svg>
);

const PhoneIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z" />
  </svg>
);

const ChatIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
  </svg>
);

const CloseIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const CopyIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

// ── CallingPanel ─────────────────────────────────────────────────
const CallingPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [copiedNumber, setCopiedNumber] = useState<string | null>(null);

  const handleCall = (number: string) => {
    window.location.href = `tel:${number.replace(/\s/g, "")}`;
  };

  const copyToClipboard = async (number: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(number.replace(/\s/g, ""));
      setCopiedNumber(number);
      setTimeout(() => setCopiedNumber(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="w-[280px] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PhoneIcon className="w-5 h-5" />
          <h3 className="font-semibold text-sm">Call Us</h3>
        </div>
        <button onClick={onClose}
          className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          aria-label="Close">
          <CloseIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="p-3 space-y-2 max-h-[300px] overflow-y-auto">
        {phoneNumbers.map((phone) => (
          <div key={phone.id} onClick={() => handleCall(phone.number)}
            className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700 cursor-pointer transition-all duration-200 flex items-center justify-between group">
            <div className="flex items-center gap-3">
              <span className="text-lg">{phone.icon}</span>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{phone.label}</p>
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                  {phone.number}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={(e) => copyToClipboard(phone.number, e)}
                className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 flex items-center justify-center transition-colors"
                title="Copy number">
                {copiedNumber === phone.number
                  ? <CheckIcon className="w-4 h-4 text-green-500" />
                  : <CopyIcon className="w-4 h-4 text-gray-600 dark:text-gray-300" />}
              </button>
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <PhoneIcon className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 text-center border-t border-gray-200 dark:border-gray-700">
        <span className="text-[10px] text-gray-400">Tap a number to call • Powered by MyListIQ</span>
      </div>
    </div>
  );
};

// ── ChoicePanel ──────────────────────────────────────────────────
const ChoicePanel: React.FC<{
  onSelectCall: () => void;
  onSelectChat: () => void;
}> = ({ onSelectCall, onSelectChat }) => (
  <div className="w-[220px] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
    <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-3">
      <h3 className="font-semibold text-sm text-center">How can we help?</h3>
    </div>

    <div className="p-3 space-y-2">
      <button onClick={onSelectCall}
        className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700 transition-all duration-200 group">
        <div className="w-9 h-9 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
          <PhoneIcon className="w-4 h-4 text-white" />
        </div>
        <div className="text-left">
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400">Call Us</p>
          <p className="text-xs text-gray-400">Talk to support</p>
        </div>
      </button>

      <button onClick={onSelectChat}
        className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-gray-700 transition-all duration-200 group">
        <div className="w-9 h-9 rounded-full bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
          <ChatIcon className="w-4 h-4 text-white" />
        </div>
        <div className="text-left">
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 group-hover:text-purple-600 dark:group-hover:text-purple-400">Chat with Us</p>
          <p className="text-xs text-gray-400">AI support chat</p>
        </div>
      </button>
    </div>

    <div className="px-4 py-2 bg-gray-50 dark:bg-gray-800 text-center border-t border-gray-200 dark:border-gray-700">
      <span className="text-[10px] text-gray-400">Powered by MyListIQ</span>
    </div>
  </div>
);

// ── BotLauncher ──────────────────────────────────────────────────
const BotLauncher: React.FC = () => {
  const [mode, setMode] = useState<Mode>("closed");

  const toggle = useCallback(() => {
    setMode((prev) => (prev === "closed" ? "choosing" : "closed"));
  }, []);

  const selectCall = useCallback(() => setMode("calling"), []);
  const selectChat = useCallback(() => setMode("chatting"), []);
  const close = useCallback(() => setMode("closed"), []);

  const isOpen = mode !== "closed";

  return (
   <div className="fixed bottom-6 right-4 z-[9999] flex flex-col items-end gap-3 max-w-[calc(100vw-1rem)]">

      {mode === "choosing" && (
        <ChoicePanel onSelectCall={selectCall} onSelectChat={selectChat} />
      )}

      {mode === "calling" && (
        <CallingPanel onClose={close} />
      )}

      {/*
        Chatbot in controlled mode:
        - hideFab=true  → hides its own toggle button
        - isOpen        → BotLauncher drives the open state directly
        - onClose       → Chatbot's minimize button calls this to reset mode
      */}
      <Chatbot
        hideFab={true}
        isOpen={mode === "chatting"}
        onClose={close}
      />

      {/* FAB */}
      <button
        onClick={toggle}
        aria-label={isOpen ? "Close support options" : "Open support options"}
        className="w-14 h-14 rounded-full shadow-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 flex items-center justify-center transition-all duration-300 ease-in-out hover:scale-110 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-300"
      >
        {isOpen
          ? <CloseIcon className="w-5 h-5 text-white" />
          : <HeadsetIcon className="w-6 h-6 text-white" />
        }
      </button>
    </div>
  );
};

export default BotLauncher;